import { axiosInstance } from "../client";
import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { CollectionResponse } from "@rumble-it/common-lib/types/api/CollectionResponse.interface";
import { PaginationParams } from "@rumble-it/common-lib/types/api/PaginationParams.interface";

interface DossierFilterParams {
  "vehicle.licensePlate.identifier"?: string;
  "driver.person.fullName"?: string;
  insurerName?: string;
  "leaseCompany.name"?: string;
  "fleetRecord.fleetClient.externalReference"?: string;
  "insuranceCompany.name"?: string;
  "exists[archivedAt]"?: boolean;
  preferredServicePartner?: boolean;
  [key: string]: any;
}

interface DossierProcessParams {
  dossierIri: string;
  processType: string; // gate-in / gate-out / notify-driver
}

export interface DossierQueryParams
  extends PaginationParams,
    DossierFilterParams {}

export const fetchDossiers = async (
  params: DossierQueryParams,
): Promise<CollectionResponse<Dossier>> =>
  axiosInstance.get("/dossiers", { params }).then((response) => response.data);

export const fetchDossierById = async (
  id: string,
  options?: { markAsRead?: boolean },
): Promise<Dossier> =>
  axiosInstance
    .get(`/dossiers/${id}`, {
      params: { markAsRead: options?.markAsRead },
    })
    .then((response) => response.data);

export const archiveDossierById = async (id: string): Promise<void> =>
  axiosInstance.post(`/admin/dossiers/${id}/archive`, {}).then(() => undefined);

export const unarchiveDossierById = async (id: string): Promise<void> =>
  axiosInstance
    .post(`/admin/dossiers/${id}/unarchive`, {})
    .then(() => undefined);

export const markDossierAsUnreadById = async (id: string): Promise<void> =>
  axiosInstance.post(`/admin/dossiers/${id}/unread`, {}).then(() => undefined);

// Grouped shape keyed by dossier status (unknown bucket for safety)
export type DossiersByStatus = { [key: string]: Dossier[] };

export const fetchDossiersGroupedByStatus =
  async (): Promise<DossiersByStatus> =>
    axiosInstance
      .get<CollectionResponse<Dossier>>("/dossiers", {
        params: {
          "exists[archivedAt]": false,
        },
      })
      .then((response) => {
        return response.data.member.reduce<DossiersByStatus>((acc, dossier) => {
          const status = dossier?.status || "unknown";
          if (!acc[status]) acc[status] = [];
          acc[status].push(dossier);
          return acc;
        }, {});
      });

interface SendDossierToServicePartnerParams {
  dossierId: string;
  servicePartnerId: string;
}

export const sendDossierToServicePartner = async ({
  dossierId,
  servicePartnerId,
}: SendDossierToServicePartnerParams): Promise<void> => {
  await axiosInstance.post(
    `/admin/dossiers/${dossierId}/send-to-service-partner/${servicePartnerId}`,
    {},
  );
};

/** The fields of the generated report the admin needs to offer it. */
export interface GeneratedDossierDocument {
  contentUrl: string;
  mimeType: string;
}

export const generateDamageIntakeReport = async (
  dossierId: string,
): Promise<GeneratedDossierDocument> =>
  axiosInstance
    .post<GeneratedDossierDocument>(
      `/admin/dossiers/${dossierId}/damage-intake-report`,
      {},
    )
    .then((response) => response.data);

export const exportDossiersBySlug = async (slug: string): Promise<void> => {
  const response = await axiosInstance.get(
    `/admin/dossiers/export/slug/${slug}`,
    {
      responseType: "blob",
      headers: { Accept: "*/*" },
    },
  );

  const contentDisposition = response.headers["content-disposition"] as
    | string
    | undefined;
  const fileNameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
  const fileName = fileNameMatch?.[1] ?? `${slug}.xlsx`;

  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const processDossierAsDealership = async (
  params: DossierProcessParams,
): Promise<Dossier> => {
  //TODO: Make this more dynamic, this is a quick fix for now and really really ugly
  const dossierIri = params.dossierIri
    .replace("/dossiers/", "/dealership/dossiers/")
    .substring(49);

  return axiosInstance
    .post(`${dossierIri}/process/${params.processType}`, {})
    .then((response) => response.data);
};
